/**
 * Vacinação (#530) — espelho do contrato da API. Ids chegam sempre como texto: o backend os
 * converte antes de responder, e a tela converte o que compara com eles.
 */

export type SituacaoVacinal = 'EM_DIA' | 'A_VENCER' | 'PENDENTE' | 'VENCIDA';

export const SITUACAO_VACINAL_LABELS: Record<SituacaoVacinal, string> = {
  EM_DIA: 'Em dia',
  A_VENCER: 'A vencer',
  PENDENTE: 'Pendente',
  VENCIDA: 'Vencida',
};

export type VaccineTypeStatus = 'ACTIVE' | 'INACTIVE';

export interface VaccineType {
  id: string;
  name: string;
  /** Meses a partir da 1ª dose; começa em 0 e o tamanho é o número de doses do esquema. */
  scheduleMonths: number[];
  /** Meses entre reforços; `null` = sem reforço. */
  boosterMonths: number | null;
  description: string | null;
  status: VaccineTypeStatus;
  createdAt: string;
  updatedAt: string;
}

/** Corpo de `POST /vaccine-types` e `PUT /vaccine-types/:id`. */
export interface VaccineTypePayload {
  name: string;
  scheduleMonths: number[];
  boosterMonths: number | null;
  description: string | null;
  status: VaccineTypeStatus;
}

export type VaccinationDoseKind = 'SCHEME' | 'BOOSTER';

export interface DoseRegistrada {
  doseKind: VaccinationDoseKind;
  doseNumber: number | null;
  appliedAt: string;
  nextDueAt: string | null;
}

export interface ResultadoSituacao {
  situacao: SituacaoVacinal;
  /** Próxima dose do esquema ou reforço; `null` se não há. */
  proximaData: string | null;
  /** Data em que ficou pendente ou vencida; `null` se "sem dose" ou em dia. */
  desde: string | null;
  ultimaDose: DoseRegistrada | null;
}

export interface Vaccination {
  id: string;
  employeeId: string;
  vaccineTypeId: string;
  doseKind: VaccinationDoseKind;
  doseNumber: number | null;
  appliedAt: string;
  lot: string | null;
  nextDueAt: string | null;
  nextDueReason: string | null;
  proofPath: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Corpo de `POST /vaccinations` e `PUT /vaccinations/:id` (multipart, arquivo `proof` opcional). */
export interface VaccinationPayload {
  employeeId: string;
  vaccineTypeId: string;
  doseKind: VaccinationDoseKind;
  doseNumber: number | null;
  appliedAt: string;
  lot: string | null;
  nextDueAt: string | null;
  nextDueReason: string | null;
}

export interface LinhaVacina {
  vaccineType: VaccineType;
  resultado: ResultadoSituacao;
  doses: Vaccination[];
}

/** Resposta de `GET /employees/:employeeId/vaccinations`. */
export interface FichaDeVacinacao {
  exigidas: LinhaVacina[];
  naoExigidas: LinhaVacina[];
}

export interface ExigenciasDaUnidade {
  unitId: string;
  unitName: string;
  vaccineTypeIds: string[];
}

/** Resposta de `GET /companies/:companyId/vaccine-requirements`. */
export interface ExigenciasDaEmpresa {
  /** Vacinas exigidas da empresa toda. */
  empresa: string[];
  /** Só as unidades que acrescentam alguma vacina. */
  unidades: ExigenciasDaUnidade[];
}

/** Corpo de `PUT /companies/:companyId/vaccine-requirements` — substitui o conjunto daquele nível. */
export interface GravacaoDeExigencias {
  unitId: string | null;
  vaccineTypeIds: string[];
}

export interface LinhaDePendencia {
  employeeId: string;
  employeeName: string;
  departmentName: string | null;
  unitName: string | null;
  vaccineTypeId: string;
  vaccineName: string;
  situacao: SituacaoVacinal;
  desde: string | null;
}

export interface FiltrosDePendencia {
  companyId?: string;
  unitId?: string;
  departmentId?: string;
  vaccineTypeId?: string;
  situacao?: SituacaoVacinal[];
  search?: string;
  page?: number;
  limit?: number;
}

/** Resposta de `GET /vaccination-pendencies`. */
export interface PaginaDePendencias {
  rows: LinhaDePendencia[];
  total: number;
  page: number;
  limit: number;
}

function juntarComE(itens: string[]): string {
  if (itens.length <= 1) return itens.join('');
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

function intervaloEmPalavras(meses: number): string {
  if (meses === 12) return 'anual';
  if (meses % 12 === 0) return `a cada ${meses / 12} anos`;
  return `a cada ${meses} meses`;
}

/** "3 doses · meses 0, 1 e 6" ou "Dose única" — a coluna de esquema do catálogo. */
export function resumoDoEsquema(scheduleMonths: number[]): string {
  if (scheduleMonths.length <= 1) return 'Dose única';
  return `${scheduleMonths.length} doses · meses ${juntarComE(scheduleMonths.map(String))}`;
}

/** "A cada 12 meses" ou "Sem reforço" — a coluna de reforço do catálogo. */
export function resumoDoReforco(boosterMonths: number | null): string {
  return boosterMonths ? `A cada ${boosterMonths} meses` : 'Sem reforço';
}

/** Legenda curta ao lado da vacina nas listas de marcar: "3 doses", "reforço anual", "dose única". */
export function legendaDaVacina(vacina: Pick<VaccineType, 'scheduleMonths' | 'boosterMonths'>): string {
  const partes: string[] = [];
  if (vacina.scheduleMonths.length > 1) partes.push(`${vacina.scheduleMonths.length} doses`);
  if (vacina.boosterMonths) partes.push(`reforço ${intervaloEmPalavras(vacina.boosterMonths)}`);
  return partes.length > 0 ? partes.join(' · ') : 'dose única';
}
